// Small labeled-control "atoms" shared by the desktop FilterBar and the
// mobile filter sheet, so the two surfaces only differ in LAYOUT, never in
// the control markup itself.

import { Field } from '@siliconx/shared/ui/Field';
import { Select } from '@siliconx/shared/ui/Select';
import { ToggleChip } from '@siliconx/shared/ui/ToggleChip';
import type { FilterBarProps } from './filterTypes';

type Props = FilterBarProps & { className?: string };

export function SearchField({ searchDraft, setSearchDraft, className }: Props) {
  return (
    <Field label="Search" className={className}>
      <input
        type="text"
        className="input w-full"
        value={searchDraft}
        onChange={(e) => { setSearchDraft(e.target.value); }}
        placeholder="Serial, id, or asset tag"
      />
    </Field>
  );
}

export function LifecycleField({ lifecycleFilter, setLifecycleFilter, lifecycleOptions, className }: Props) {
  return (
    <Field label="Lifecycle state" className={className}>
      <Select
        className="w-full"
        value={lifecycleFilter}
        onChange={(e) => { setLifecycleFilter(e.target.value); }}
      >
        {lifecycleOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Select>
    </Field>
  );
}

export function ConditionField({ conditionFilter, setConditionFilter, conditionOptions, className }: Props) {
  return (
    <Field label="Condition" className={className}>
      <Select
        className="w-full"
        value={conditionFilter}
        onChange={(e) => { setConditionFilter(e.target.value); }}
      >
        {conditionOptions.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Select>
    </Field>
  );
}

export function ManufacturerField({ manufacturerFilter, setManufacturerFilter, manufacturerOptions, className }: Props) {
  return (
    <Field label="Manufacturer" className={className}>
      <Select
        className="w-full"
        value={manufacturerFilter}
        onChange={(e) => { setManufacturerFilter(e.target.value); }}
      >
        <option value="">All manufacturers</option>
        {manufacturerOptions.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </Select>
    </Field>
  );
}

export function ModelField({ modelDraft, setModelDraft, className }: Props) {
  return (
    <Field label="Model" className={className}>
      <input
        type="text"
        className="input w-full"
        value={modelDraft}
        onChange={(e) => { setModelDraft(e.target.value); }}
        placeholder="e.g. H100"
      />
    </Field>
  );
}

export function LocationField({ locationFilter, setLocationFilter, locations, className }: Props) {
  return (
    <Field label="Location" className={className}>
      <Select
        className="w-full"
        value={locationFilter}
        onChange={(e) => { setLocationFilter(e.target.value); }}
      >
        <option value="">All locations</option>
        {(locations ?? []).map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </Select>
    </Field>
  );
}

export function LotField({ lotFilter, setLotFilter, lots, className }: Props) {
  return (
    <Field label="Lot" className={className}>
      <Select
        className="w-full"
        value={lotFilter}
        onChange={(e) => { setLotFilter(e.target.value); }}
      >
        <option value="">All lots</option>
        {(lots ?? []).map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </Select>
    </Field>
  );
}

/** Admin-only. Caller decides whether to render this at all — it never
 *  reserves an empty slot in a non-admin's bar. */
export function TeamField({ teamFilter, setTeamFilter, teams, className }: Props) {
  return (
    <Field label="Team" className={className}>
      <Select
        className="w-full"
        value={teamFilter}
        onChange={(e) => { setTeamFilter(e.target.value); }}
      >
        <option value="">All teams</option>
        {(teams ?? []).map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </Select>
    </Field>
  );
}

/** Boolean facet as a toggle chip — a checkbox reads as a form input among
 *  selects; a pill reads as a filter. */
export function MissingFinancialsToggle({
  missingFinancialsOnly,
  setMissingFinancialsOnly,
  className,
}: Props) {
  return (
    <ToggleChip
      active={missingFinancialsOnly}
      onClick={() => { setMissingFinancialsOnly(!missingFinancialsOnly); }}
      className={className}
    >
      Missing financials
    </ToggleChip>
  );
}
